import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Bot, Plus, Settings, Clock, CheckCircle, ListTodo, Activity, Cog,
  Shield, ChevronRight, ChevronDown, Copy, Trash2, Play, Pause, Archive, X,
  Send, Terminal, Zap, Eye, Lock, Unlock, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

// Import data
import { initialAgents, generateMockTasks, generateActivityLog } from '../data/agentData';
import { 
  placeholderDepartments, 
  getCategoryOrder, 
  departmentsWithCategories,
  departmentConfig 
} from '../data/departmentConfig';

// Import components
import {
  PasswordScreen,
  DepartmentCard,
  PlaceholderDepartmentCard,
  DepartmentControls,
  HiddenDepartmentsPanel,
  AgentListPanel,
  PlaceholderDepartmentView,
  NewAgentDialog,
  NewTaskDialog,
  SettingsDialog,
  StatusBadge,
  PriorityBadge
} from '../components/rhino-1';

const AgentWorkforce = () => {
  // Core state
  const [agents, setAgents] = useState(initialAgents);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');
  const [tasks, setTasks] = useState({});
  const [activityLogs, setActivityLogs] = useState({});

  // Authentication
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Dialog states
  const [newAgentDialogOpen, setNewAgentDialogOpen] = useState(false);
  const [newTaskDialogOpen, setNewTaskDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  // Department visibility controls
  const [allDepartmentsCollapsed, setAllDepartmentsCollapsed] = useState(false);
  const [futureDepartmentsCollapsed, setFutureDepartmentsCollapsed] = useState(false);
  const [editDepartmentMode, setEditDepartmentMode] = useState(false);
  const [collapsedDepartments, setCollapsedDepartments] = useState({});
  const [hiddenDepartments, setHiddenDepartments] = useState(() => {
    const saved = localStorage.getItem('rhino1_hidden_departments');
    return saved ? JSON.parse(saved) : {};
  });
  const [selectedPlaceholder, setSelectedPlaceholder] = useState(null);

  // Command input state
  const [commandInput, setCommandInput] = useState('');

  // Form states
  const [newAgentForm, setNewAgentForm] = useState({
    name: '', role: 'Sales Agent', description: '', status: 'active', personaPrompt: ''
  });
  const [newTaskForm, setNewTaskForm] = useState({
    title: '', description: '', priority: 'normal', linkToRecord: ''
  });

  // Initialize tasks and logs on mount
  useEffect(() => {
    const initialTasks = {};
    const initialLogs = {};
    initialAgents.forEach(agent => {
      initialTasks[agent.id] = generateMockTasks(agent.id);
      initialLogs[agent.id] = generateActivityLog(agent.id);
    });
    setTasks(initialTasks);
    setActivityLogs(initialLogs);
  }, []);

  // Save hidden departments
  useEffect(() => {
    localStorage.setItem('rhino1_hidden_departments', JSON.stringify(hiddenDepartments));
  }, [hiddenDepartments]);

  // Department visibility helpers
  const toggleDepartmentVisibility = (deptId) => {
    setHiddenDepartments(prev => ({ ...prev, [deptId]: !prev[deptId] }));
  };

  const toggleDepartmentCollapse = (deptId) => {
    setCollapsedDepartments(prev => ({ ...prev, [deptId]: !prev[deptId] }));
  };

  const isDepartmentCollapsed = (deptId) => {
    return allDepartmentsCollapsed || collapsedDepartments[deptId];
  };

  // Filter agents
  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          agent.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || agent.status === statusFilter;
    const matchesRole = roleFilter === 'all' || agent.role === roleFilter;
    const matchesDepartment = departmentFilter === 'all' || agent.department === departmentFilter;
    return matchesSearch && matchesStatus && matchesRole && matchesDepartment;
  });

  // Get unique values for filters
  const uniqueRoles = [...new Set(agents.map(a => a.role))];
  const uniqueDepartments = [...new Set(agents.map(a => a.department || 'General'))];
  const categoryOrder = getCategoryOrder(departmentFilter);

  // Sort agents
  const sortedFilteredAgents = [...filteredAgents].sort((a, b) => {
    if (departmentsWithCategories.includes(departmentFilter)) {
      const currentCategoryOrder = getCategoryOrder(departmentFilter);
      const catA = currentCategoryOrder.indexOf(a.category) >= 0 ? currentCategoryOrder.indexOf(a.category) : 999;
      const catB = currentCategoryOrder.indexOf(b.category) >= 0 ? currentCategoryOrder.indexOf(b.category) : 999;
      return catA - catB;
    }
    if (a.department !== b.department) {
      const deptOrder = ['General', 'Marketing', 'Operations', 'Sales', 'Call Center', 'Human Resources', 'Finance & Accounting'];
      return deptOrder.indexOf(a.department) - deptOrder.indexOf(b.department);
    }
    return a.name.localeCompare(b.name);
  });

  // Calculate department counts
  const getDeptCounts = (deptName) => {
    const total = agents.filter(a => a.department === deptName).length;
    const active = agents.filter(a => a.department === deptName && a.status === 'active').length;
    return { total, active, inactive: total - active };
  };

  const activeCount = agents.filter(a => a.status === 'active').length;

  // Build department data for cards
  const activeDepartments = Object.keys(departmentConfig).map(deptId => {
    const config = departmentConfig[deptId];
    const counts = getDeptCounts(deptId);
    return {
      id: deptId,
      name: config.name,
      icon: config.icon,
      emoji: config.emoji,
      ...counts,
      isActive: true
    };
  });

  const departments = activeDepartments;

  // Agent handlers
  const handleCreateAgent = () => {
    if (!newAgentForm.name || !newAgentForm.role) {
      toast.error('Please fill in required fields');
      return;
    }

    const newAgent = {
      id: `agent-${Date.now()}`,
      name: newAgentForm.name,
      role: newAgentForm.role,
      department: 'General',
      description: newAgentForm.description,
      status: newAgentForm.status,
      createdBy: 'Ryan March',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      personaPrompt: newAgentForm.personaPrompt,
      capabilities: [],
      modulesAssigned: [],
      permissions: { canReadCRM: true, canWriteCRM: false, canSendNotifications: false, canDraftEmails: false },
      limits: { maxTasksPerHour: 50, maxConcurrentTasks: 5 },
      tasksInQueue: 0,
      tasksToday: 0,
      tasksCompleted7Days: 0,
      avgResponseTime: 'N/A'
    };

    setAgents([...agents, newAgent]);
    setTasks({ ...tasks, [newAgent.id]: [] });
    setActivityLogs({ 
      ...activityLogs, 
      [newAgent.id]: [{
        id: `log-${newAgent.id}-1`,
        timestamp: new Date().toISOString(),
        eventType: 'Agent Created',
        description: 'New agent created by Super Admin',
        triggeredBy: 'Super Admin'
      }]
    });
    setSelectedAgent(newAgent);
    setNewAgentDialogOpen(false);
    setNewAgentForm({ name: '', role: 'Sales Agent', description: '', status: 'active', personaPrompt: '' });
    toast.success('Agent created successfully');
  };

  const handleAddTask = () => {
    if (!newTaskForm.title || !selectedAgent) {
      toast.error('Please fill in required fields');
      return;
    }

    const newTask = {
      id: `task-${selectedAgent.id}-${Date.now()}`,
      agentId: selectedAgent.id,
      title: newTaskForm.title,
      description: newTaskForm.description,
      source: 'Manual',
      priority: newTaskForm.priority,
      status: 'queued',
      createdAt: new Date().toISOString(),
      completedAt: null,
      result: null,
      notes: null
    };

    setTasks({ ...tasks, [selectedAgent.id]: [newTask, ...(tasks[selectedAgent.id] || [])] });
    setAgents(agents.map(a => a.id === selectedAgent.id ? { ...a, tasksInQueue: a.tasksInQueue + 1 } : a));
    setActivityLogs({
      ...activityLogs,
      [selectedAgent.id]: [{
        id: `log-${selectedAgent.id}-${Date.now()}`,
        timestamp: new Date().toISOString(),
        eventType: 'Task Created',
        description: `Manual task "${newTaskForm.title}" assigned`,
        triggeredBy: 'Super Admin'
      }, ...(activityLogs[selectedAgent.id] || [])]
    });
    setNewTaskDialogOpen(false);
    setNewTaskForm({ title: '', description: '', priority: 'normal', linkToRecord: '' });
    toast.success('Task added to queue');
  };

  const handleStatusChange = (agentId, newStatus) => {
    setAgents(agents.map(a => a.id === agentId ? { ...a, status: newStatus, updatedAt: new Date().toISOString() } : a));
    if (selectedAgent?.id === agentId) {
      setSelectedAgent({ ...selectedAgent, status: newStatus });
    }
    setActivityLogs({
      ...activityLogs,
      [agentId]: [{
        id: `log-${agentId}-${Date.now()}`,
        timestamp: new Date().toISOString(),
        eventType: 'Status Changed',
        description: `Status changed to ${newStatus}`,
        triggeredBy: 'Super Admin'
      }, ...(activityLogs[agentId] || [])]
    });
    toast.success(`Agent status updated to ${newStatus}`);
  };

  const handleCloneAgent = (agent) => {
    const clonedAgent = {
      ...agent,
      id: `agent-${Date.now()}`,
      name: `${agent.name} (Copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tasksInQueue: 0,
      tasksToday: 0,
      tasksCompleted7Days: 0
    };
    setAgents([...agents, clonedAgent]);
    setTasks({ ...tasks, [clonedAgent.id]: [] });
    setActivityLogs({
      ...activityLogs,
      [clonedAgent.id]: [{
        id: `log-${clonedAgent.id}-1`,
        timestamp: new Date().toISOString(),
        eventType: 'Agent Created',
        description: `Agent cloned from ${agent.name}`,
        triggeredBy: 'Super Admin'
      }]
    });
    toast.success('Agent cloned successfully');
  };

  const handleArchiveAgent = (agentId) => {
    setAgents(agents.filter(a => a.id !== agentId));
    if (selectedAgent?.id === agentId) {
      setSelectedAgent(null);
    }
    toast.success('Agent archived');
  };

  // Handler for sending commands to agents
  const handleSendCommand = () => {
    if (!commandInput.trim() || !selectedAgent) {
      toast.error('Please enter a command');
      return;
    }

    // Create a task from the command
    const newTask = {
      id: `task-${selectedAgent.id}-${Date.now()}`,
      agentId: selectedAgent.id,
      title: commandInput,
      description: `Command issued by Super Admin`,
      source: 'Direct Command',
      priority: 'high',
      status: 'queued',
      createdAt: new Date().toISOString(),
      completedAt: null,
      result: null,
      notes: null
    };

    setTasks({ ...tasks, [selectedAgent.id]: [newTask, ...(tasks[selectedAgent.id] || [])] });
    setAgents(agents.map(a => a.id === selectedAgent.id ? { ...a, tasksInQueue: a.tasksInQueue + 1 } : a));
    setActivityLogs({
      ...activityLogs,
      [selectedAgent.id]: [{
        id: `log-${selectedAgent.id}-${Date.now()}`,
        timestamp: new Date().toISOString(),
        eventType: 'Command Received',
        description: `Direct command: "${commandInput}"`,
        triggeredBy: 'Super Admin'
      }, ...(activityLogs[selectedAgent.id] || [])]
    });
    
    setCommandInput('');
    toast.success(`Command sent to ${selectedAgent.name}`);
  };

  // Render password screen if not authenticated
  if (!isAuthenticated) {
    return (
      <Layout>
        <PasswordScreen onAuthenticate={setIsAuthenticated} />
      </Layout>
    );
  }

  // Get placeholder department if selected
  const selectedPlaceholderDept = selectedPlaceholder 
    ? placeholderDepartments.find(d => d.id === selectedPlaceholder) 
    : null;

  return (
    <Layout>
      <div className="h-[calc(100vh-140px)] flex flex-col overflow-hidden">
        {/* Top Header Bar */}
        <div className="flex items-center justify-between mb-4 shrink-0">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Bot className="h-7 w-7 text-primary" />
              Rhino-1 Enterprise
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              AI-powered workforce management for Rhino Shield operations.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs bg-primary/10">
              <Shield className="h-3 w-3 mr-1" />
              Developer Access
            </Badge>
            <Button variant="outline" size="sm" onClick={() => setSettingsDialogOpen(true)}>
              <Settings className="h-4 w-4 mr-1" />
              Settings
            </Button>
            <Button size="sm" onClick={() => setNewAgentDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              New Agent
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setIsAuthenticated(false)}>
              <Shield className="h-4 w-4 mr-1" />
              Lock
            </Button>
          </div>
        </div>

        {/* Department Controls */}
        <DepartmentControls
          activeDepartmentsCount={activeDepartments.length}
          plannedDepartmentsCount={placeholderDepartments.filter(d => !hiddenDepartments[d.id]).length}
          allDepartmentsCollapsed={allDepartmentsCollapsed}
          editDepartmentMode={editDepartmentMode}
          onToggleCollapse={() => setAllDepartmentsCollapsed(!allDepartmentsCollapsed)}
          onToggleEditMode={() => setEditDepartmentMode(!editDepartmentMode)}
        />

        {/* Collapsible Department Sections */}
        <div className={`shrink-0 transition-all duration-300 ${allDepartmentsCollapsed ? 'max-h-24 overflow-hidden' : ''}`}>
          {/* Active Department Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-3">
            {activeDepartments.filter(dept => !hiddenDepartments[dept.id]).map((dept) => (
              <DepartmentCard
                key={dept.id}
                dept={dept}
                isSelected={departmentFilter === dept.id}
                isCollapsed={isDepartmentCollapsed(dept.id)}
                allDepartmentsCollapsed={allDepartmentsCollapsed}
                editDepartmentMode={editDepartmentMode}
                onSelect={(id) => {
                  setDepartmentFilter(departmentFilter === id ? 'all' : id);
                  setSelectedPlaceholder(null);
                }}
                onToggleCollapse={toggleDepartmentCollapse}
                onToggleVisibility={toggleDepartmentVisibility}
              />
            ))}
          </div>

          {/* Placeholder Departments - Collapsible */}
          {!allDepartmentsCollapsed && (
            <div className="mb-3">
              <button 
                onClick={() => setFutureDepartmentsCollapsed(!futureDepartmentsCollapsed)}
                className="flex items-center gap-2 mb-2 w-full text-left hover:bg-accent/50 rounded px-2 py-1 transition-colors"
              >
                {futureDepartmentsCollapsed ? (
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                <h3 className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  Future Departments
                  <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded">
                    {placeholderDepartments.filter(dept => !hiddenDepartments[dept.id]).length}
                  </span>
                </h3>
                <div className="flex-1 h-px bg-border" />
              </button>
              
              {!futureDepartmentsCollapsed && (
                <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-2">
                  {placeholderDepartments.filter(dept => !hiddenDepartments[dept.id]).map((dept) => (
                    <PlaceholderDepartmentCard
                      key={dept.id}
                      dept={dept}
                      isSelected={selectedPlaceholder === dept.id}
                      isCollapsed={isDepartmentCollapsed(dept.id)}
                      editDepartmentMode={editDepartmentMode}
                      onSelect={(id) => {
                        setSelectedPlaceholder(selectedPlaceholder === id ? null : id);
                        setDepartmentFilter('all');
                        setSelectedAgent(null);
                      }}
                      onToggleCollapse={toggleDepartmentCollapse}
                      onToggleVisibility={toggleDepartmentVisibility}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Hidden Departments Recovery */}
        {editDepartmentMode && (
          <HiddenDepartmentsPanel
            hiddenDepartments={hiddenDepartments}
            activeDepartments={activeDepartments}
            placeholderDepartments={placeholderDepartments}
            onToggleVisibility={toggleDepartmentVisibility}
          />
        )}

        {/* Show All Departments Button */}
        {departmentFilter !== 'all' && (
          <div className="mb-3 shrink-0">
            <Button variant="outline" size="sm" onClick={() => setDepartmentFilter('all')}>
              <X className="h-4 w-4 mr-1" />
              Clear Filter - Show All Departments
            </Button>
          </div>
        )}

        {/* Main Content - Two Column Layout */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-0 overflow-hidden">
          {/* Left Panel - Agent List */}
          <AgentListPanel
            agents={agents}
            sortedFilteredAgents={sortedFilteredAgents}
            selectedAgent={selectedAgent}
            setSelectedAgent={setSelectedAgent}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            roleFilter={roleFilter}
            setRoleFilter={setRoleFilter}
            departmentFilter={departmentFilter}
            setDepartmentFilter={setDepartmentFilter}
            departments={departments}
            uniqueDepartments={uniqueDepartments}
            uniqueRoles={uniqueRoles}
            activeCount={activeCount}
            categoryOrder={categoryOrder}
            onNewAgentClick={() => setNewAgentDialogOpen(true)}
          />

          {/* Right Panel - Agent Detail */}
          <Card 
            className="lg:col-span-2 flex flex-col overflow-hidden border-2 border-primary/30"
            style={{ boxShadow: '0 0 15px rgba(var(--primary), 0.3), 0 0 30px rgba(var(--primary), 0.15)' }}
          >
            {selectedAgent ? (
              <>
                {/* Agent Header */}
                <CardHeader className="border-b">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <Bot className="h-8 w-8 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <h2 className="text-xl font-bold">{selectedAgent.name}</h2>
                          <Select 
                            value={selectedAgent.status} 
                            onValueChange={(val) => handleStatusChange(selectedAgent.id, val)}
                          >
                            <SelectTrigger className="w-[130px] h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="paused">Paused</SelectItem>
                              <SelectItem value="training">In Training</SelectItem>
                              <SelectItem value="offline">Offline</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-muted-foreground">{selectedAgent.role}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="flex items-center gap-1">
                            <ListTodo className="h-4 w-4 text-blue-500" />
                            Queue: {selectedAgent.tasksInQueue}
                          </span>
                          <span className="flex items-center gap-1">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            7 Days: {selectedAgent.tasksCompleted7Days}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4 text-orange-500" />
                            Avg: {selectedAgent.avgResponseTime}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setNewTaskDialogOpen(true)}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add Task
                      </Button>
                      <Button variant="outline" size="icon" onClick={() => handleCloneAgent(selectedAgent)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="icon" className="text-red-500" onClick={() => handleArchiveAgent(selectedAgent.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                {/* Agent Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
                  <TabsList className="px-4 pt-2 justify-start bg-transparent shrink-0">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="commands">Commands</TabsTrigger>
                    <TabsTrigger value="tasks">Tasks</TabsTrigger>
                    <TabsTrigger value="config">Configuration</TabsTrigger>
                    <TabsTrigger value="activity">Activity Log</TabsTrigger>
                  </TabsList>

                  <div className="flex-1 overflow-y-auto p-4 min-h-0">
                    <TabsContent value="overview" className="mt-0 space-y-4">
                      {/* Agent Role & Description */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Bot className="h-4 w-4 text-primary" />
                            Agent Profile
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <p className="text-sm text-muted-foreground">{selectedAgent.description}</p>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div><span className="text-muted-foreground">Department:</span> <span className="font-medium">{selectedAgent.department}</span></div>
                            <div><span className="text-muted-foreground">Role:</span> <span className="font-medium">{selectedAgent.role}</span></div>
                            <div><span className="text-muted-foreground">Created by:</span> <span className="font-medium">{selectedAgent.createdBy}</span></div>
                            <div><span className="text-muted-foreground">Category:</span> <span className="font-medium">{selectedAgent.category || 'General'}</span></div>
                            <div><span className="text-muted-foreground">Created:</span> <span className="font-medium">{new Date(selectedAgent.createdAt).toLocaleDateString()}</span></div>
                            <div><span className="text-muted-foreground">Updated:</span> <span className="font-medium">{new Date(selectedAgent.updatedAt).toLocaleDateString()}</span></div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Capabilities - Enhanced */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Zap className="h-4 w-4 text-yellow-500" />
                            Capabilities & Actions
                          </CardTitle>
                          <CardDescription>What this agent can do</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="flex flex-wrap gap-2">
                            {selectedAgent.capabilities?.map((cap, i) => (
                              <Badge key={i} variant="secondary" className="text-xs py-1">
                                <CheckCircle className="h-3 w-3 mr-1 text-green-500" />
                                {cap}
                              </Badge>
                            ))}
                            {(!selectedAgent.capabilities || selectedAgent.capabilities.length === 0) && (
                              <p className="text-sm text-muted-foreground">No capabilities configured</p>
                            )}
                          </div>
                          
                          {/* Modules Assigned */}
                          {selectedAgent.modulesAssigned && selectedAgent.modulesAssigned.length > 0 && (
                            <div className="border-t pt-4">
                              <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                <Terminal className="h-4 w-4 text-blue-500" />
                                Modules Assigned
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {selectedAgent.modulesAssigned.map((mod, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    {mod}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Permissions Quick View */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Shield className="h-4 w-4 text-primary" />
                            Permissions
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 gap-2">
                            {Object.entries(selectedAgent.permissions || {}).map(([key, value]) => (
                              <div key={key} className="flex items-center justify-between p-2 bg-muted/50 rounded text-xs">
                                <span className="flex items-center gap-1">
                                  {value ? <Unlock className="h-3 w-3 text-green-500" /> : <Lock className="h-3 w-3 text-red-500" />}
                                  {key.replace(/([A-Z])/g, ' $1').replace('can', '').trim()}
                                </span>
                                <Badge variant={value ? 'default' : 'secondary'} className="text-[10px]">
                                  {value ? 'Yes' : 'No'}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    {/* NEW: Commands Tab */}
                    <TabsContent value="commands" className="mt-0 space-y-4">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Terminal className="h-4 w-4 text-primary" />
                            Send Command to Agent
                          </CardTitle>
                          <CardDescription>
                            Issue direct commands to {selectedAgent.name}. Commands will be added to the task queue.
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={commandInput}
                              onChange={(e) => setCommandInput(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleSendCommand()}
                              placeholder={`Tell ${selectedAgent.name} what to do...`}
                              className="flex-1 px-4 py-2 bg-background border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                            <Button onClick={handleSendCommand} disabled={!commandInput.trim()}>
                              <Send className="h-4 w-4 mr-1" />
                              Send
                            </Button>
                          </div>
                          
                          {/* Quick Commands */}
                          <div>
                            <h4 className="text-sm font-medium mb-2">Quick Commands</h4>
                            <div className="flex flex-wrap gap-2">
                              {[
                                'Generate report',
                                'Check pending items',
                                'Send status update',
                                'Process queue',
                                'Run diagnostics'
                              ].map((cmd, i) => (
                                <Button 
                                  key={i} 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => setCommandInput(cmd)}
                                  className="text-xs"
                                >
                                  {cmd}
                                </Button>
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Recent Commands */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <MessageSquare className="h-4 w-4 text-primary" />
                            Recent Commands
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ScrollArea className="h-[200px]">
                            {(activityLogs[selectedAgent.id] || [])
                              .filter(log => log.eventType === 'Command Received' || log.eventType === 'Task Created')
                              .slice(0, 10)
                              .map(log => (
                                <div key={log.id} className="flex items-start gap-3 p-2 border-b last:border-0">
                                  <Terminal className="h-4 w-4 text-muted-foreground mt-0.5" />
                                  <div className="flex-1">
                                    <p className="text-sm">{log.description}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {new Date(log.timestamp).toLocaleString()}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            {(activityLogs[selectedAgent.id] || [])
                              .filter(log => log.eventType === 'Command Received' || log.eventType === 'Task Created')
                              .length === 0 && (
                              <p className="text-sm text-muted-foreground text-center py-4">
                                No commands sent yet
                              </p>
                            )}
                          </ScrollArea>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="tasks" className="mt-0 space-y-4">
                      <Card>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-base">Current Queue</CardTitle>
                            <Badge>{tasks[selectedAgent.id]?.filter(t => t.status === 'queued').length || 0} pending</Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <ScrollArea className="h-[200px]">
                            {(tasks[selectedAgent.id] || []).filter(t => t.status !== 'completed').map(task => (
                              <div key={task.id} className="flex items-center justify-between p-3 border-b last:border-0">
                                <div>
                                  <p className="font-medium text-sm">{task.title}</p>
                                  <p className="text-xs text-muted-foreground">{task.source}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <PriorityBadge priority={task.priority} />
                                  <StatusBadge status={task.status} />
                                </div>
                              </div>
                            ))}
                          </ScrollArea>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="config" className="mt-0 space-y-4">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Permissions & Limits</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            {Object.entries(selectedAgent.permissions || {}).map(([key, value]) => (
                              <div key={key} className="flex items-center justify-between p-2 border rounded">
                                <span className="text-sm">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                                <Badge variant={value ? 'default' : 'secondary'}>{value ? 'Yes' : 'No'}</Badge>
                              </div>
                            ))}
                          </div>
                          <div className="border-t pt-4">
                            <h4 className="text-sm font-medium mb-2">Rate Limits</h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>Max Tasks/Hour: <span className="font-medium">{selectedAgent.limits?.maxTasksPerHour}</span></div>
                              <div>Max Concurrent: <span className="font-medium">{selectedAgent.limits?.maxConcurrentTasks}</span></div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </TabsContent>

                    <TabsContent value="activity" className="mt-0">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Activity Log</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ScrollArea className="h-[400px]">
                            {(activityLogs[selectedAgent.id] || []).map(log => (
                              <div key={log.id} className="flex items-start gap-3 p-3 border-b last:border-0">
                                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                  {log.eventType === 'Task Completed' ? <CheckCircle className="h-4 w-4" /> :
                                   log.eventType === 'Status Changed' ? <Activity className="h-4 w-4" /> :
                                   log.eventType === 'Config Updated' ? <Cog className="h-4 w-4" /> :
                                   <ListTodo className="h-4 w-4" />}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium text-sm">{log.eventType}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {new Date(log.timestamp).toLocaleString()}
                                    </span>
                                  </div>
                                  <p className="text-sm text-muted-foreground">{log.description}</p>
                                  <p className="text-xs text-muted-foreground mt-1">By: {log.triggeredBy}</p>
                                </div>
                              </div>
                            ))}
                          </ScrollArea>
                        </CardContent>
                      </Card>
                    </TabsContent>
                  </div>
                </Tabs>
              </>
            ) : selectedPlaceholderDept ? (
              <PlaceholderDepartmentView department={selectedPlaceholderDept} />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <Bot className="h-16 w-16 text-muted-foreground/30 mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground">Select an Agent</h3>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  Choose an agent from the list to view details and manage tasks.
                </p>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Dialogs */}
      <NewAgentDialog
        open={newAgentDialogOpen}
        onOpenChange={setNewAgentDialogOpen}
        newAgentForm={newAgentForm}
        setNewAgentForm={setNewAgentForm}
        onCreateAgent={handleCreateAgent}
      />

      <NewTaskDialog
        open={newTaskDialogOpen}
        onOpenChange={setNewTaskDialogOpen}
        selectedAgent={selectedAgent}
        newTaskForm={newTaskForm}
        setNewTaskForm={setNewTaskForm}
        onAddTask={handleAddTask}
      />

      <SettingsDialog
        open={settingsDialogOpen}
        onOpenChange={setSettingsDialogOpen}
      />
    </Layout>
  );
};

export default AgentWorkforce;
